/**
 * TOT — custom language selector for the header top row.
 *
 * Horizon ships the language picker as a native <select> (snippets/localization-form.liquid),
 * so the open state is the OS dropdown and cannot be styled or dimmed behind. This element
 * progressively enhances that select: the real <select> stays in the DOM as the form control
 * (name="language_code") and keeps working if this script never runs — we only hide it
 * visually and drive it from a custom button + listbox.
 *
 * Submitting goes straight through the native form rather than dispatching a synthetic
 * 'change' at Horizon's declarative on:change binding — one less thing to break on a theme
 * update, and the form already carries return_to/country_code.
 */
class TotLanguageSelect extends HTMLElement {
  connectedCallback() {
    if (this.dataset.enhanced) return;

    this.select = this.querySelector('select.localization-form__select');
    if (!this.select || this.select.options.length < 2) return;

    this.dataset.enhanced = 'true';
    this.open = false;
    this.build();

    // this.list is hoisted to <body>, so `this.contains` alone would treat a click on
    // the panel's own padding as an outside click and close it mid-choice.
    this.onDocumentClick = (event) => {
      if (!this.contains(event.target) && !this.list.contains(event.target)) this.close();
    };
    this.onKeydown = (event) => this.handleKeydown(event);
    // Fixed positioning does not follow the sticky header; re-pinning on every scroll
    // frame is jitter for no benefit, so the panel just closes.
    this.onReflow = () => this.close();
  }

  disconnectedCallback() {
    this.close();
    this.overlay?.remove();
    this.list?.remove();
  }

  build() {
    const current = this.select.options[this.select.selectedIndex];

    // The native control still posts the form; it just stops being the visible UI.
    // .visually-hidden (not display:none) so it keeps its accessible name and value.
    this.select.classList.add('visually-hidden');
    this.select.setAttribute('tabindex', '-1');
    this.select.setAttribute('aria-hidden', 'true');
    this.querySelector('.language-selector .icon-caret')?.setAttribute('hidden', '');

    this.button = document.createElement('button');
    this.button.type = 'button';
    this.button.className = 'tot-lang__button';
    this.button.setAttribute('aria-expanded', 'false');
    this.button.setAttribute('aria-haspopup', 'listbox');
    this.button.setAttribute('aria-label', this.select.getAttribute('aria-label') || 'Language');
    this.button.innerHTML =
      `<span class="tot-lang__current"></span><span class="tot-lang__caret" aria-hidden="true"></span>`;
    this.button.querySelector('.tot-lang__current').textContent = current ? current.textContent.trim() : '';
    if (current) this.button.prepend(this.flag(current.value));
    this.button.addEventListener('click', () => this.toggle());

    this.list = document.createElement('ul');
    this.list.className = 'tot-lang__list';
    this.list.setAttribute('role', 'listbox');
    this.list.hidden = true;

    this.options = Array.from(this.select.options).map((option, index) => {
      const item = document.createElement('li');
      item.className = 'tot-lang__option';
      item.setAttribute('role', 'option');
      item.setAttribute('tabindex', '-1');
      item.setAttribute('aria-selected', String(index === this.select.selectedIndex));
      item.textContent = option.textContent.trim();
      item.prepend(this.flag(option.value));
      item.addEventListener('click', () => this.choose(index));
      this.list.append(item);
      return item;
    });

    this.overlay = document.createElement('div');
    this.overlay.className = 'tot-lang__overlay';
    this.overlay.hidden = true;
    this.overlay.addEventListener('click', () => this.close());

    const wrapper = document.createElement('div');
    wrapper.className = 'tot-lang';
    wrapper.append(this.button);
    this.querySelector('.language-selector')?.append(wrapper);

    // ⚠ Both the tint AND the panel live on <body>, not in the header.
    // sections/header.liquid is `position: sticky; z-index: 8`, which makes it a
    // stacking context — any z-index on a descendant is capped inside it, so a panel
    // left in the header renders UNDER a body-level overlay no matter how high its
    // z-index goes. Raising the header instead would lift the whole bar out of the
    // tint. Hoisting the panel keeps "everything dims except the dropdown" true, at
    // the cost of positioning it manually in position().
    document.body.append(this.overlay, this.list);
  }

  /**
   * Flag for a language code, as an <img> pointing at the asset URL that
   * sections/header.liquid passed down (see the comment there for why not inline SVG).
   * Returns an empty text node for a language with no flag configured, so callers can
   * prepend unconditionally.
   */
  flag(isoCode) {
    const src = this.getAttribute(`data-flag-${String(isoCode).toLowerCase()}`);
    if (!src) return document.createTextNode('');

    const img = document.createElement('img');
    img.className = 'tot-lang__flag';
    img.src = src;
    img.alt = '';
    img.setAttribute('aria-hidden', 'true');
    img.loading = 'lazy';
    return img;
  }

  /** Pin the hoisted panel under the button. Fixed coords, so it must be recomputed
   *  on every open — the sticky header moves the button as the page scrolls. */
  position() {
    const rect = this.button.getBoundingClientRect();
    this.list.style.top = `${rect.bottom + 8}px`;
    this.list.style.right = `${document.documentElement.clientWidth - rect.right}px`;
    this.list.style.minWidth = `${rect.width}px`;
  }

  toggle() {
    this.open ? this.close() : this.openSelector();
  }

  openSelector() {
    this.open = true;
    this.position();
    this.list.hidden = false;
    this.overlay.hidden = false;
    this.classList.add('tot-lang--open');
    this.button.setAttribute('aria-expanded', 'true');
    // Deferred: this click is still bubbling, and binding now would immediately close it.
    requestAnimationFrame(() => document.addEventListener('click', this.onDocumentClick));
    document.addEventListener('keydown', this.onKeydown);
    window.addEventListener('scroll', this.onReflow, { passive: true });
    window.addEventListener('resize', this.onReflow);
    this.options[this.select.selectedIndex]?.focus();
  }

  close() {
    if (!this.open) return;
    this.open = false;
    this.list.hidden = true;
    this.overlay.hidden = true;
    this.classList.remove('tot-lang--open');
    this.button.setAttribute('aria-expanded', 'false');
    document.removeEventListener('click', this.onDocumentClick);
    document.removeEventListener('keydown', this.onKeydown);
    window.removeEventListener('scroll', this.onReflow);
    window.removeEventListener('resize', this.onReflow);
  }

  handleKeydown(event) {
    const focused = this.options.indexOf(document.activeElement);

    switch (event.key) {
      case 'Escape':
        event.preventDefault();
        this.close();
        this.button.focus();
        break;
      case 'ArrowDown':
        event.preventDefault();
        this.options[Math.min(focused + 1, this.options.length - 1)]?.focus();
        break;
      case 'ArrowUp':
        event.preventDefault();
        this.options[Math.max(focused - 1, 0)]?.focus();
        break;
      case 'Enter':
      case ' ':
        if (focused > -1) {
          event.preventDefault();
          this.choose(focused);
        }
        break;
    }
  }

  choose(index) {
    if (index === this.select.selectedIndex) {
      this.close();
      this.button.focus();
      return;
    }

    this.select.selectedIndex = index;
    this.close();
    this.select.form?.submit();
  }
}

if (!customElements.get('tot-language-select')) {
  customElements.define('tot-language-select', TotLanguageSelect);
}
